import { Link } from "react-router-dom";
import { KPI, KPIGrid, Badge, Table, Card, Pagination, Page, LoadingState, ErrorState, Text } from "@marekl/adminer/components";
import { BarChart } from "../../components/BarChart";
import { formatDateTime, formatNumber, formatWeekday } from "@marekl/adminer/format";
import {
  useReportAggregate,
  type DailyBucket,
  type ReportRidesResponse,
  type RideDetailItem,
  type TypeBreakdown,
} from "../../lib/queries/reports";
import type { UseOffsetPaginatedQueryResult } from "@marekl/adminer/hooks";
import styles from "./ReportsPage.module.css";

interface AggregateContentProps {
  query: ReturnType<typeof useReportAggregate>;
}

export function AggregateContent({ query }: AggregateContentProps) {
  if (!query.data && query.isFetching) return <LoadingState />;
  if (query.isError)
    return (
      <ErrorState
        error="Nepodařilo se načíst report."
        onRetry={query.refetch}
      />
    );
  if (!query.data) return null;

  const { totals, series: seriesRaw, breakdown: breakdownRaw, granularity } = query.data;
  const isHourly = granularity === "hourly";
  const series: DailyBucket[] = seriesRaw ?? [];
  const breakdown: TypeBreakdown[] = breakdownRaw ?? [];

  const chartData = series.map((b: DailyBucket) => ({
    label: isHourly ? `${b.day.format("H")}h` : b.day.format("D.M."),
    tooltipLabel: isHourly ? b.day.format("H:mm") : b.day.format("D.M.YYYY"),
    value: b.rides,
  }));

  const breakdownColumns = [
    { key: "code", header: "Kód", render: (r: TypeBreakdown) => r.code },
    { key: "name", header: "Název", render: (r: TypeBreakdown) => r.name },
    {
      key: "rides",
      header: "Jízd",
      render: (r: TypeBreakdown) => formatNumber(r.rides),
      tdClass: styles.numCell,
    },
    {
      key: "customers",
      header: "Zákazníků",
      render: (r: TypeBreakdown) => formatNumber(r.customers),
      tdClass: styles.numCell,
    },
    {
      key: "vouchers",
      header: "Voucherů",
      render: (r: TypeBreakdown) => formatNumber(r.vouchers),
      tdClass: styles.numCell,
    },
  ];

  const seriesColumns = [
    {
      key: "day",
      header: isHourly ? "Hodina" : "Datum",
      render: (b: DailyBucket) => (
        <Text variant="mono">{isHourly ? b.day.format("H:mm") : b.day.format("D.M.YYYY")}</Text>
      ),
    },
    ...(!isHourly
      ? [
          {
            key: "weekday",
            header: "Den",
            render: (b: DailyBucket) => formatWeekday(b.day),
          },
        ]
      : []),
    {
      key: "rides",
      header: "Jízd",
      render: (b: DailyBucket) => formatNumber(b.rides),
      tdClass: styles.numCell,
    },
    {
      key: "customers",
      header: "Zákazníků",
      render: (b: DailyBucket) => formatNumber(b.customers),
      tdClass: styles.numCell,
    },
    {
      key: "vouchers",
      header: "Voucherů",
      render: (b: DailyBucket) => formatNumber(b.vouchers_issued),
      tdClass: styles.numCell,
    },
  ];

  return (
    <>
      <KPIGrid className={styles.kpis}>
        <KPI label="Jízd celkem" value={formatNumber(totals.rides)} />
        <KPI label="Odbavení zákazníci" value={formatNumber(totals.customers)} />
        <KPI label="Voucherů recepce" value={formatNumber(totals.counter_vouchers)} />
        <KPI label="Voucherů e-shop" value={formatNumber(totals.eshop_vouchers)} />
      </KPIGrid>

      <div className={styles.twoCol}>
        <Card
          header={<Card.Header title={isHourly ? "Jízdy podle hodiny" : "Jízdy podle dne"} />}
          bodyPadding="sm"
        >
          <BarChart
            data={chartData}
            valueFormatter={(v) => `${formatNumber(v)} jízd`}
            aria-label={isHourly ? "Graf jízd podle hodiny" : "Graf jízd podle dne"}
          />
        </Card>
        <Card
          header={<Card.Header title="Přehled podle typu" />}
          bodyPadding="none"
        >
          <Table
            columns={breakdownColumns}
            rows={breakdown}
            rowKey={(r) => r.type_id}
            empty={<Text variant="muted">Žádná data</Text>}
          />
        </Card>
      </div>

      {!isHourly && (
        <Card
          header={<Card.Header title="Denní přehled" />}
          bodyPadding="none"
        >
          <Table
            columns={seriesColumns}
            rows={series}
            rowKey={(b) => b.day.toISOString()}
            empty={<Text variant="muted">Žádná data</Text>}
          />
        </Card>
      )}
    </>
  );
}

interface RidesContentProps {
  query: UseOffsetPaginatedQueryResult<
    ReportRidesResponse & { items: RideDetailItem[] }
  >;
}

export function RidesContent({ query }: RidesContentProps) {
  if (!query.data && query.isFetching) return <LoadingState />;
  if (!query.data && !query.isError) return null;

  const items = query.data?.items ?? [];

  return (
    <>
      <Page.SectionHeader
        title="Detailní výpis jízd"
        meta={query.pagination.summary}
      />
      <Table
        cards
        columns={[
          {
            key: "time",
            header: "Čas",
            render: (r) => (
              <Text variant="mono">{formatDateTime(r.ride_created_at)}</Text>
            ),
          },
          {
            key: "voucher",
            header: "Voucher",
            render: (r) => (
              <Link to={`/vouchers/${r.voucher_id}`} className={styles.mono}>
                {r.voucher_barcode}
              </Link>
            ),
          },
          { key: "customer", header: "Zákazník", primary: true, render: (r) => r.customer_name },
          {
            key: "type",
            header: "Typ",
            render: (r) => `${r.voucher_type_code} — ${r.voucher_type_name}`,
          },
          { key: "user", header: "Recepční", render: (r) => r.display_name || r.username },
          {
            key: "status",
            header: "Stav",
            render: (r) =>
              r.cancelled ? (
                <Badge variant="err">Zrušena</Badge>
              ) : (
                <Badge variant="ok">Provedena</Badge>
              ),
          },
        ]}
        rows={items}
        rowKey={(r) => r.ride_id}
        rowStatus={(r) => (r.cancelled ? "err" : undefined)}
        error={query.isError ? "Nepodařilo se načíst výpis jízd." : undefined}
        onRetry={query.refetch}
        empty={<Text variant="muted">Žádné záznamy</Text>}
      />
      <Pagination
        {...query.pagination}
      />
    </>
  );
}
